import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { buildMailtoUrl } from "../lib/api";
import { toast } from "./ui/sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const contactFormSchema = z.object({
  name: z.string().trim().min(2, "Please enter your name."),
  company: z.string().trim().max(120, "Company name is too long.").optional(),
  email: z.string().trim().email("Please enter a valid email address."),
  message: z.string().trim().min(10, "Please share a short message."),
});

const ContactForm = () => {
  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = (values) => {
    try {
      const mailtoUrl = buildMailtoUrl(values);
      window.location.href = mailtoUrl;
      toast.success("Opening your email app", {
        description: "Your message has been prepared for Hailo Digital.",
      });
      form.reset();
    } catch (error) {
      toast.error("Unable to open your email app", {
        description: "Please email admin@hailodigital.com directly.",
      });
    }
  };

  return (
    <Form {...form}>
      <form className="contact-form" data-testid="contact-form" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="field-grid">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="form-label" data-testid="contact-form-name-label">
                  Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="form-input"
                    data-testid="contact-form-name-input"
                    placeholder="Your name"
                  />
                </FormControl>
                <FormMessage data-testid="contact-form-name-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="form-label" data-testid="contact-form-company-label">
                  Company
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="form-input"
                    data-testid="contact-form-company-input"
                    placeholder="Company name"
                  />
                </FormControl>
                <FormMessage data-testid="contact-form-company-error" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="form-label" data-testid="contact-form-email-label">
                Email
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="form-input"
                  data-testid="contact-form-email-input"
                  placeholder="name@company.com"
                  type="email"
                />
              </FormControl>
              <FormMessage data-testid="contact-form-email-error" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="form-label" data-testid="contact-form-message-label">
                Message
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  className="form-textarea"
                  data-testid="contact-form-message-input"
                  placeholder="Tell us about your inquiry."
                />
              </FormControl>
              <FormMessage data-testid="contact-form-message-error" />
            </FormItem>
          )}
        />

        <Button
          className="primary-button"
          data-testid="contact-form-submit-button"
          size="lg"
          type="submit"
        >
          Send Inquiry
        </Button>
      </form>
    </Form>
  );
};

export default ContactForm;